import { ElementType } from 'react';

import {
  IcAddLinkIcon,
  IcBoldIcon,
  IcItalicIcon,
  IcStrikethroughIcon,
  IcUnderlineIcon,
} from '@gem/icons';

import { ITextFormattingMenuViewParams, ViewType } from '../TextFormattingMenu.types';

interface IActionItem {
  key: string;
  icon: ElementType;
  isActive: boolean;
  hasDivider: boolean;
  onClick: () => void;
}

interface ITextFormattingMainViewPresenter {
  actionList: IActionItem[];
  checkRangeToTag(range: Range, tagNames: string[]): boolean;
}

export const useTextFormattingMainViewPresenter = (
  data: ITextFormattingMenuViewParams
): ITextFormattingMainViewPresenter => {
  const {
    selectionRange,
    onBold,
    onItalic,
    onUnderline,
    onStrikeThrough,
    onRemoveLink,
    onChangeView,
  } = data;

  const presenter: ITextFormattingMainViewPresenter = {
    get actionList(): IActionItem[] {
      const isLinkActive = presenter.checkRangeToTag(selectionRange, ['a']);

      return [
        {
          key: 'bold',
          icon: IcBoldIcon,
          isActive: presenter.checkRangeToTag(selectionRange, ['b', 'strong']),
          hasDivider: false,
          onClick: onBold,
        },
        {
          key: 'italic',
          icon: IcItalicIcon,
          isActive: presenter.checkRangeToTag(selectionRange, ['i', 'em']),
          hasDivider: false,
          onClick: onItalic,
        },
        {
          key: 'underline',
          icon: IcUnderlineIcon,
          isActive: isLinkActive || presenter.checkRangeToTag(selectionRange, ['u']),
          hasDivider: false,
          onClick: onUnderline,
        },
        {
          key: 'strikeThrough',
          icon: IcStrikethroughIcon,
          isActive: presenter.checkRangeToTag(selectionRange, ['s', 'strike']),
          hasDivider: false,
          onClick: onStrikeThrough,
        },
        {
          key: 'link',
          icon: IcAddLinkIcon,
          isActive: isLinkActive,
          hasDivider: true,
          onClick: () => (isLinkActive ? onRemoveLink() : onChangeView(ViewType.Link)),
        },
      ];
    },

    checkRangeToTag(range: Range, tagNames: string[]): boolean {
      if (!range.startContainer.parentNode || !range.endContainer.parentNode) {
        return false;
      }

      return tagNames.some((tag) => {
        const start = (range.startContainer.parentNode as Element).closest(tag);
        const end = (range.endContainer.parentNode as Element).closest(tag);

        if (!start || !end) {
          return false;
        }

        return start === end;
      });
    },
  };

  return presenter;
};
